import {t, itemName} from './locales.js';
import {layout as L} from './layout.js';

export const ITEMS = Object.freeze(['driver', 'pliers', 'frame', 'differential', 'breaker-a', 'breaker-b']);
export const REQUIRED = Object.freeze(ITEMS.filter(id => id !== 'pliers'));
export const STAGES = Object.freeze(['drive', 'turn', 'park', 'van-door', 'exit', 'recall', 'brief', 'follow', 'toolbox', 'collect', 'ready', 'walk-to-board', 'demo', 'practice', 'fasten', 'debrief', 'turn-on-light', 'complete']);
export const ARRIVAL_DURATIONS = Object.freeze({approach:14, park:4, exit:2.8});

export function initialState() {
  return {stage: 'drive', time: 0, totalTime: 0, idleTime: 0, held: null, introIndex: 0, driveDistance: 0, houseDoorOpen: false,
    placed: {}, installed: [], screw: 0, feedback: null, hintLevel: 0};
}
export function isDialogue(state) {
  return ['recall', 'brief', 'ready', 'debrief', 'complete'].includes(state.stage);
}
const enter = (s, stage) => ({...s, stage, time: 0, idleTime: 0, hintLevel: 0, feedback: null});
const progress = s => ({...s, idleTime: 0, hintLevel: 0, feedback: null});
const canHandle = s => ['collect', 'practice', 'fasten'].includes(s.stage);
// The reducer independently enforces the same conservative support footprint as
// the ray query; callers cannot inject floor, case or overlapping placements.
export function benchPlacement(item,p,placed={}) {
  const q=L.itemDimensions[item],b=L.bench,tb=L.toolbox;
  if(!q||!p||!['x','y','z'].every(k=>Number.isFinite(p[k]))||Math.abs(p.y-b.top)>1e-6)return false;
  const hw=q.width/2+.006,hd=q.depth/2+.006;
  if(p.x-hw<b.x-b.width/2||p.x+hw>b.x+b.width/2||p.z-hd<b.z-b.depth/2||p.z+hd>b.z+b.depth/2)return false;
  const overlap=s=>p.x+hw>s.minX&&p.x-hw<s.maxX&&p.z+hd>s.minZ&&p.z-hd<s.maxZ;
  if(overlap({minX:tb.x-tb.width/2,maxX:tb.x+tb.width/2,minZ:tb.z-tb.depth/2,maxZ:tb.z+tb.depth/2}))return false;
  if(L.solids.some(s=>s.id!=='bench'&&s.minY<p.y+q.height&&s.minY+s.height>p.y+1e-5&&overlap(s)))return false;
  return !Object.entries(placed).some(([id,other])=>{const d=L.itemDimensions[id];return id!==item&&d&&Math.abs(other.y-p.y)<.01&&Math.abs(other.x-p.x)<(d.width+q.width)/2+.008&&Math.abs(other.z-p.z)<(d.depth+q.depth)/2+.008;});
}
const onBench=(s,id)=>Object.hasOwn(s.placed,id)&&benchPlacement(id,s.placed[id],s.placed);
export function guidedPosition(id,p){
 const target=L.guidedKit.targets[id],d=L.guidedKit.tolerance;
 return !!target&&!!p&&Number.isFinite(p.x)&&Number.isFinite(p.z)&&Math.abs(p.x-target.x)<=d+1e-9&&Math.abs(p.z-target.z)<=d+1e-9;
}
export function demoKitReady(s){
 return s.held===null&&!Object.hasOwn(s.placed,'pliers')&&REQUIRED.every(id=>onBench(s,id)&&guidedPosition(id,s.placed[id]));
}
const feedback = (s, key) => s.feedback === key ? s : {...s, feedback: key};

export function transition(state, action = {}) {
  if (!action || typeof action !== 'object') return state;
  if (action.type === 'RESET') return initialState();
  if (action.type === 'TICK') {
    if (!Number.isFinite(action.dt) || action.dt <= 0 || action.paused || action.active === false || state.stage === 'complete') return state;
    const dt = Math.min(action.dt, .1);
    let next = {...state, time: state.time + dt, totalTime: state.totalTime + dt, idleTime: (state.idleTime || 0) + dt};
    if (canHandle(next)) next.hintLevel = Math.min(3, Math.floor((next.idleTime + 1e-8) / 18));
    if (next.stage === 'drive') next.driveDistance = (state.driveDistance || 0) + dt * L.route.speed;
    else if (next.stage === 'turn' && next.time >= ARRIVAL_DURATIONS.approach - 1e-8) next = enter(next, 'park');
    else if (next.stage === 'park' && next.time >= ARRIVAL_DURATIONS.park - 1e-8) next = enter(next, 'van-door');
    else if (next.stage === 'exit' && next.time >= ARRIVAL_DURATIONS.exit - 1e-8) next = enter(next, 'recall');
    return next;
  }
  switch (action.type) {
    case 'SKIP_DRIVE': return ['drive', 'turn', 'park'].includes(state.stage) ? enter(state, 'van-door') : state;
    case 'INTRO_NEXT': return state.stage === 'drive' ? (state.introIndex >= 2 ? enter(state, 'turn') : {...progress(state), introIndex: (state.introIndex || 0) + 1}) : state;
    case 'REPLY': return state.stage === 'recall' && ['recap', 'remind'].includes(action.choice) ? enter(state, 'brief') : state;
    case 'CONTINUE': {
      const next = {brief: 'follow', ready: 'walk-to-board', debrief: 'turn-on-light'}[state.stage];
      return next ? enter(state, next) : state;
    }
    case 'FOLLOW_ARRIVED': return state.stage === 'follow' && state.houseDoorOpen ? enter(state, 'toolbox') : state;
    case 'BOARD_ARRIVED': return state.stage === 'walk-to-board' ? enter(state, 'demo') : state;
    case 'SELECT': {
      if (state.stage === 'turn-on-light' && action.target === 'light-switch') return enter(state, 'complete');
      if (state.stage === 'van-door' && action.target === 'van-door') return enter(state, 'exit');
      if (state.stage === 'follow' && action.target === 'house-door') return state.houseDoorOpen ? state : progress({...state, houseDoorOpen:true});
      if (state.stage === 'toolbox' && action.target === 'toolbox') return enter(state, 'collect');
      const target = action.target;
      if (typeof target !== 'string') return state;
      if (target === 'mentor' && state.stage === 'follow') return feedback(state, state.houseDoorOpen ? 'talk-follow-enter' : 'talk-follow-door');
      if (target === 'mentor' && state.stage === 'practice') return feedback(state, state.held === 'breaker-b' ? 'talk-practice-place' : state.held === null ? 'talk-practice-take' : 'talk-practice-free-hand');
      if (target === 'mentor' && state.stage === 'fasten') return feedback(state, state.held === 'driver' ? 'talk-fasten-hold' : state.held === null ? 'talk-fasten-take' : 'talk-fasten-free-hand');
      if (target.startsWith('item:')) {
        const item = target.slice(5);
        return transition(state, {type: 'TAKE', item});
      }
      if (target.startsWith('drop:') && Object.hasOwn(state.placed, target.slice(5))) return transition(state, {type: 'TAKE', item: target.slice(5)});
      if (['slot:frame', 'slot:differential', 'slot:breaker-a', 'slot:breaker-b'].includes(target)) return transition(state, {type: 'INSTALL', item: state.held, slot: target.slice(5)});
      if (target === 'screw' && state.stage === 'fasten') return feedback(state, state.held === 'driver' ? 'hold-driver' : 'need-driver');
      return state;
    }
    case 'TAKE': {
      const item = action.item;
      if (!canHandle(state) || !ITEMS.includes(item) || state.held !== null || state.installed.includes(item)) return state;
      const placed = {...state.placed};
      delete placed[item];
      return progress({...state, held: item, placed});
    }
    case 'DROP': {
      const {item, point} = action;
      if (!canHandle(state) || state.held !== item || !benchPlacement(item,point,state.placed)) return state;
      return progress({...state, held: null,
        placed: {...state.placed, [item]: {x: point.x, y: point.y, z: point.z}}});
    }
    case 'RETURN': {
      const item = action.item;
      if (!canHandle(state) || state.held !== item) return state;
      return progress({...state, held: null});
    }
    case 'CHECK_KIT': {
      if (state.stage !== 'collect') return state;
      if (state.held !== null) return feedback(state, 'hand-full');
      if (Object.hasOwn(state.placed,'pliers')) return feedback(state, 'kit-extra');
      if (!REQUIRED.every(id => onBench(state,id))) return feedback(state, 'kit-missing');
      const misplaced=REQUIRED.find(id=>!guidedPosition(id,state.placed[id]));
      if(misplaced)return feedback(state, `kit-position:${misplaced}`);
      return enter(state, 'ready');
    }
    case 'DEMO_DONE': {
      if (state.stage !== 'demo' || action.physical !== true || state.held !== null || !REQUIRED.every(id => onBench(state,id))) return state;
      const demonstrated = ['frame', 'differential', 'breaker-a'];
      const placed={...state.placed};for(const id of demonstrated)delete placed[id];
      return enter({...state, placed, installed: demonstrated}, 'practice');
    }
    case 'INSTALL': {
      if (state.stage !== 'practice') return state;
      const slot = String(action.slot || '').replace(/^slot:/, '');
      if (slot !== 'breaker-b') return feedback(state, 'wrong-slot');
      if (action.item !== 'breaker-b' || state.held !== 'breaker-b') return feedback(state, 'select-breaker');
      if (!['frame', 'differential', 'breaker-a'].every(id => state.installed.includes(id))) return state;
      return enter({...state, held: null,
        installed: [...state.installed, 'breaker-b']}, 'fasten');
    }
    case 'TURN': {
      if (state.stage !== 'fasten' || !Number.isFinite(action.delta) || action.delta <= 0 || action.delta > .1) return state;
      if (state.held !== 'driver') return feedback(state, 'need-driver');
      const screw = Math.min(1, state.screw + action.delta);
      const next = progress({...state, screw: screw >= 1 - 1e-8 ? 1 : screw});
      return next.screw === 1 ? enter(next, 'debrief') : next;
    }
    default: return state;
  }
}

function hintFor(state, lang) {
  const level = Math.min(3, Math.max(0, Math.trunc(state.hintLevel || 0)));
  if (!level || !canHandle(state)) return '';
  if (state.stage === 'collect' && level === 3) {
    if (Object.hasOwn(state.placed,'pliers')) return t(lang, 'hint.collect.extra');
    const missing = REQUIRED.filter(id => !onBench(state,id));
    if(missing.length)return t(lang, 'hint.collect.3') + missing.map(id => itemName(lang, id)).join(', ') + '.';
    const misplaced=REQUIRED.find(id=>!guidedPosition(id,state.placed[id]));
    if(misplaced)return t(lang,'feedback.kit-position')+itemName(lang,misplaced)+t(lang,'feedback.kit-position-end');
    if(state.held!==null)return t(lang,'feedback.hand-full');
    return t(lang, 'hint.collect.ready');
  }
  if (state.stage === 'practice' && level >= 2 && state.held !== 'breaker-b') {
    return t(lang, Object.hasOwn(state.placed, 'breaker-b') ? 'hint.practice.recover' : 'hint.practice.take');
  }
  return t(lang, `hint.${state.stage}.${level}`);
}

function questFor(state,lang,stage){
  const objectives=[];
  if(stage==='collect'){
    const correctlyPlaced=id=>onBench(state,id)&&guidedPosition(id,state.placed[id]);
    for(const id of ['driver','frame','differential']){const current=Number(correctlyPlaced(id));objectives.push({id,label:itemName(lang,id),current,total:1,complete:current===1});}
    const current=Number(correctlyPlaced('breaker-a'))+Number(correctlyPlaced('breaker-b'));
    objectives.push({id:'breakers',label:t(lang,'quest.breakers'),current,total:2,complete:current===2});
  }
  const extra=stage==='collect'&&Object.hasOwn(state.placed,'pliers')?t(lang,'quest.extra'):'';
  const ready=stage==='collect'&&demoKitReady(state);
  const guide=L.guidedKit.colors[state.held];
  const heldGoal=stage==='collect'&&guide ? t(lang,'quest.guide-prefix')+itemName(lang,state.held)+t(lang,'quest.guide-middle')+guide.labels[lang==='en'?'en':'fr']+t(lang,'quest.guide-end') : null;
  return {title:t(lang,`${stage}.title`),goal:heldGoal??t(lang,state.held&&canHandle(state)?(stage==='practice'&&state.held==='breaker-b'?'practice.goal':stage==='fasten'&&state.held==='driver'?'fasten.goal':'quest.place'):ready?'quest.check':stage==='practice'&&state.held===null?'quest.take-breaker':stage==='fasten'&&state.held===null?'quest.take-driver':`${stage}.goal`),objectives,extra};
}

export function model(state, lang = 'fr') {
  const stage = STAGES.includes(state.stage) ? state.stage : 'drive';
  const buttons = [];
  const button = (action, key = action) => buttons.push({action, label: t(lang, key)});
  if (stage === 'drive') button('intro-next');
  if (['drive', 'turn', 'park'].includes(stage)) button('skip-drive');
  if (stage === 'recall') {
    button('reply:recap', 'reply-recap');
    button('reply:remind', 'reply-remind');
  }
  if (stage === 'brief') button('continue');
  if (stage === 'ready') button('continue','continue-board');

  if (stage === 'debrief') button('continue');
  if (stage === 'complete') button('reset');
  let bodyKey = `${stage}.body`;
  if (stage === 'drive' && state.introIndex > 0) bodyKey = `drive.intro.${Math.min(2,state.introIndex)}`;

  return {
    title: t(lang, `${stage}.title`), goal: t(lang, `${stage}.goal`), speaker: t(lang, 'camille'), body: t(lang, bodyKey),
    buttons, dialogue: isDialogue(state), hand:{item:state.held,name:state.held?itemName(lang,state.held):''},
    quest:questFor(state,lang,stage),
    hint: hintFor(state, lang), feedback: state.feedback?.startsWith('kit-position:') ? t(lang,'feedback.kit-position')+itemName(lang,state.feedback.slice(13))+t(lang,'feedback.kit-position-end') : state.feedback ? t(lang, `feedback.${state.feedback}`) : '',
    progress: STAGES.indexOf(stage) / (STAGES.length - 1)
  };
}

// Scene-space panels share the same gates/actions; physical targets stay physical.
// At most two choices: room remains for the XR adapter's session controls.
export function xrModel(state, lang = 'fr') {
  const m = model(state, lang), buttons = [...m.buttons];
  return {title: m.title, body: [m.body, m.hint].filter(Boolean).join('\n'), image: false, feedback: m.feedback, buttons};
}